import { type FC, useCallback, useEffect, useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import type { LiveReactionEmojiType } from "@/types/graphql/site.generated";
import { useLiveReactions } from "../model/useLiveReactions";
import { readPanelCollapsed, writePanelCollapsed } from "../lib/panelCollapsed";
import { LiveReactionsLayer } from "./LiveReactionsLayer";
import { LiveReactionsPanel } from "./LiveReactionsPanel";

export interface LiveReactionsOverlayProps {
  contentId: string;
  phrases: string[];
  emojis: LiveReactionEmojiType[];
  controlsVisible: boolean;
  isCasting: boolean;
}

/**
 * Живые реакции поверх эфира (спека live-reactions-and-polls, «Витрина»).
 * Монтируется только для STARTED-эфира при флаге LIVE_REACTIONS и тумблере
 * партнёра; во время Chromecast скрыт.
 */
export const LiveReactionsOverlay: FC<LiveReactionsOverlayProps> = ({
  contentId,
  phrases,
  emojis,
  controlsVisible,
  isCasting,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const { items, react, removeItem, recentTotal } = useLiveReactions({
    contentId,
    phrasesCount: phrases.length,
    allowedEmojis: emojis,
    // Во время каста слой полёта не смонтирован: значки некому удалять.
    animate: !reduceMotion && !isCasting,
  });

  // Читаем после монтирования: на сервере хранилища нет, и первая
  // отрисовка должна совпасть с SSR (раскрытая панель).
  const [collapsed, setCollapsed] = useState(false);
  // На мобайле по умолчанию свёрнута: одно сердечко в кадре, строка
  // эмодзи раскрывается по нажатию. Выбор зрителя запоминается.
  useEffect(() => {
    setCollapsed(readPanelCollapsed() ?? isMobile);
  }, [isMobile]);
  const toggleCollapsed = useCallback(() => {
    setCollapsed((current) => {
      writePanelCollapsed(!current);
      return !current;
    });
  }, []);

  if (isCasting) return null;

  return (
    <>
      {!reduceMotion && (
        <LiveReactionsLayer
          items={items}
          panelOnRight={!isMobile}
          phrases={phrases}
          onDone={removeItem}
        />
      )}
      <LiveReactionsPanel
        phrases={phrases}
        emojis={emojis}
        onReact={react}
        orientation={isMobile ? "horizontal" : "vertical"}
        visible={controlsVisible}
        quietCount={reduceMotion ? recentTotal : null}
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
      />
    </>
  );
};
